import type { CanvasGroup, CanvasSegment, GroupBounds, ConnectorSide, SegmentEndpoint, EntityRectMap } from './types';
import { NODE_WIDTH, NODE_HEIGHT, COLLAPSED_GROUP_WIDTH, COLLAPSED_GROUP_HEIGHT, resolveEndpointPosition } from './types';

function endpointKey(ep: SegmentEndpoint): string {
  return `${ep.entityId}:${ep.side}`;
}

// ============================================
// 1. Compute which nodes and groups are hidden by collapsed groups
// ============================================

export interface CollapseHiddenResult {
  hiddenNodeIds: Set<string>;
  hiddenGroupIds: Set<string>;
  /** Maps each hidden node to the collapsed root group that hides it */
  hiddenNodeToGroupId: Map<string, string>;
}

export function computeCollapseHidden(groups: CanvasGroup[]): CollapseHiddenResult {
  const hiddenNodeIds = new Set<string>();
  const hiddenGroupIds = new Set<string>();
  const hiddenNodeToGroupId = new Map<string, string>();
  const groupMap = new Map(groups.map(g => [g.id, g]));

  function hideAllMembers(group: CanvasGroup, rootCollapsedId: string) {
    for (const nodeId of group.memberNodeIds) {
      hiddenNodeIds.add(nodeId);
      hiddenNodeToGroupId.set(nodeId, rootCollapsedId);
    }
    for (const childGroupId of group.memberGroupIds) {
      hiddenGroupIds.add(childGroupId);
      const child = groupMap.get(childGroupId);
      if (child) hideAllMembers(child, rootCollapsedId);
    }
  }

  // First pass: collapsed groups hide all their descendants
  for (const g of groups) {
    if (g.collapsed && !hiddenGroupIds.has(g.id)) hideAllMembers(g, g.id);
  }

  // Second pass: hide non-collapsed groups whose ALL member nodes are hidden
  // (covers groups visually inside a collapsed parent but not explicitly nested).
  // Collapsed groups themselves stay visible as compact boxes.
  for (const g of groups) {
    if (hiddenGroupIds.has(g.id)) continue;
    if (g.collapsed) continue;
    if (g.memberNodeIds.length === 0) continue;
    if (g.memberNodeIds.every(id => hiddenNodeIds.has(id))) {
      hiddenGroupIds.add(g.id);
    }
  }

  return { hiddenNodeIds, hiddenGroupIds, hiddenNodeToGroupId };
}

// ============================================
// 2. Build augmented node map with virtual nodes for collapsed groups
// ============================================

/**
 * Adds per-side virtual nodes (`__grp__<id>__left` etc.) for each visible
 * collapsed group so that edges can attach to the collapsed box.
 * The positions are adjusted so that `getConnectorPoint(x, y, side)` (which
 * assumes NODE_WIDTH × NODE_HEIGHT) returns the correct point on the
 * COLLAPSED_GROUP_WIDTH × COLLAPSED_GROUP_HEIGHT box.
 */
export function buildAugmentedNodeMap(
  nodeMap: Map<string, { x: number; y: number }>,
  groups: CanvasGroup[],
  groupBoundsMap: Map<string, GroupBounds>,
  hiddenGroupIds: Set<string>,
): Map<string, { x: number; y: number }> {
  const map = new Map(nodeMap);
  const CW = COLLAPSED_GROUP_WIDTH, CH = COLLAPSED_GROUP_HEIGHT;
  const NW = NODE_WIDTH, NH = NODE_HEIGHT;

  for (const g of groups) {
    if (!g.collapsed) continue;
    if (hiddenGroupIds.has(g.id)) continue;
    const bounds = groupBoundsMap.get(g.id);
    if (!bounds) continue;
    const gx = bounds.x, gy = bounds.y;
    map.set(`__grp__${g.id}__left`,   { x: gx,                    y: gy + CH / 2 - NH / 2 });
    map.set(`__grp__${g.id}__right`,  { x: gx + CW - NW,          y: gy + CH / 2 - NH / 2 });
    map.set(`__grp__${g.id}__top`,    { x: gx + CW / 2 - NW / 2,  y: gy });
    map.set(`__grp__${g.id}__bottom`, { x: gx + CW / 2 - NW / 2,  y: gy + CH - NH });
  }

  return map;
}

// ============================================
// 3. Reroute segments whose endpoints are hidden by collapse
// ============================================

function bestSide(
  groupId: string,
  otherEp: SegmentEndpoint,
  groupBoundsMap: Map<string, GroupBounds>,
  entityRectMap: EntityRectMap,
): ConnectorSide {
  const bounds = groupBoundsMap.get(groupId);
  if (!bounds) return 'left';
  const otherPos = resolveEndpointPosition(otherEp, entityRectMap);
  if (!otherPos) return 'left';
  const cx = bounds.x + COLLAPSED_GROUP_WIDTH / 2;
  const cy = bounds.y + COLLAPSED_GROUP_HEIGHT / 2;
  const dx = otherPos.x - cx;
  const dy = otherPos.y - cy;
  if (Math.abs(dx) > Math.abs(dy)) return dx > 0 ? 'right' : 'left';
  return dy > 0 ? 'bottom' : 'top';
}

function rerouteEndpoint(
  groupId: string,
  otherEp: SegmentEndpoint,
  groupBoundsMap: Map<string, GroupBounds>,
  entityRectMap: EntityRectMap,
): SegmentEndpoint {
  const side = bestSide(groupId, otherEp, groupBoundsMap, entityRectMap);
  return { entityId: `__grp__${groupId}__${side}`, side };
}

export function rerouteCollapsedSegments(
  segments: CanvasSegment[],
  hiddenNodeToGroupId: Map<string, string>,
  groupBoundsMap: Map<string, GroupBounds>,
  entityRectMap: EntityRectMap,
): CanvasSegment[] {
  const result: CanvasSegment[] = [];
  const dedup = new Set<string>();

  for (const s of segments) {
    const fromGroupId = hiddenNodeToGroupId.get(s.from.entityId) ?? null;
    const toGroupId = hiddenNodeToGroupId.get(s.to.entityId) ?? null;

    // Neither endpoint hidden → keep as-is
    if (!fromGroupId && !toGroupId) { result.push(s); continue; }

    // Both in same collapsed group → internal edge, hide
    if (fromGroupId && toGroupId && fromGroupId === toGroupId) continue;

    // Reroute hidden endpoints to collapsed group virtual node
    const newFrom = fromGroupId
      ? rerouteEndpoint(fromGroupId, s.to, groupBoundsMap, entityRectMap)
      : s.from;
    const newTo = toGroupId
      ? rerouteEndpoint(toGroupId, s.from, groupBoundsMap, entityRectMap)
      : s.to;

    // Dedup: one visual edge per (rerouted-from, rerouted-to) pair
    const fk = endpointKey(newFrom);
    const tk = endpointKey(newTo);
    const key = `${fk}->${tk}`;
    if (dedup.has(key)) continue;
    dedup.add(key);

    result.push({ ...s, id: `rerouted-${s.id}`, from: newFrom, to: newTo });
  }

  return result;
}
